import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "@tanstack/react-router";
import { Dialog } from "@base-ui/react/dialog";
import { Menu } from "@base-ui/react/menu";
import { CaretDownIcon, CheckIcon, PlusIcon, SignInIcon } from "@phosphor-icons/react";
import { BrandMark } from "@withmethod/design";
import { api, type Workspace } from "./api";
import { OTHER_WORKSPACE_EVENT } from "./workspace-events";
import { Button } from "./components/ui/button";

/** Open another workspace of the person: the browser signs in to it, and the dashboard shows that workspace. */
export function switchWorkspace(workspace: Pick<Workspace, "id">) {
  window.location.assign(api.switchWorkspaceUrl(workspace.id));
}

/**
 * The workspace control at the top of the sidebar. It names the current workspace and lists the person's other
 * workspaces, the invitations to their email, and New workspace.
 */
export function WorkspaceMenu({ current }: { current: string | undefined }) {
  const list = useQuery({ queryKey: ["workspaces"], queryFn: api.workspaces });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const join = async (id: string) => {
    setError("");
    try { switchWorkspace(await api.joinWorkspace(id)); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "Could not join the workspace."); }
  };
  return (
    <>
      <Menu.Root>
        <Menu.Trigger className="workspace-switch" aria-label={`Workspace: ${current ?? "Method"}`}>
          <span className="workspace-mark"><BrandMark /></span>
          <span>
            <strong>Method</strong>
            <small>{current ?? "Method"}</small>
          </span>
          <CaretDownIcon size={12} />
        </Menu.Trigger>
        <Menu.Portal>
          <Menu.Positioner side="bottom" align="start" sideOffset={6}>
            <Menu.Popup className="dash-menu workspace-menu">
              {list.data?.workspaces.map((workspace) => (
                <Menu.Item key={workspace.id} onClick={() => { if (!workspace.current) switchWorkspace(workspace); }}>
                  <span className="workspace-menu-check">{workspace.current && <CheckIcon size={14} />}</span>
                  {workspace.name}
                </Menu.Item>
              ))}
              {list.data?.invitations.map((invitation) => (
                <Menu.Item key={invitation.id} onClick={() => void join(invitation.id)}>
                  <SignInIcon size={14} />
                  Join {invitation.workspace}
                </Menu.Item>
              ))}
              <Menu.Separator className="workspace-menu-separator" />
              <Menu.Item onClick={() => setCreating(true)}>
                <PlusIcon size={14} />
                New workspace
              </Menu.Item>
            </Menu.Popup>
          </Menu.Positioner>
        </Menu.Portal>
      </Menu.Root>
      {error && <p role="alert" className="workspace-menu-error">{error}</p>}
      <NewWorkspaceDialog open={creating} onClose={() => setCreating(false)} />
    </>
  );
}

function NewWorkspaceDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  return (
    <Dialog.Root open={open} onOpenChange={(next) => { if (!next && !saving) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Backdrop className="dash-backdrop" />
        <Dialog.Popup className="dash-dialog">
          <Dialog.Title>New workspace</Dialog.Title>
          <form
            onSubmit={async (event) => {
              event.preventDefault();
              if (!name.trim()) return;
              setSaving(true);
              setError("");
              try { switchWorkspace(await api.createWorkspace(name.trim())); }
              catch (reason) { setError(reason instanceof Error ? reason.message : "Could not make the workspace."); setSaving(false); }
            }}
          >
            <p className="list-muted">A workspace has its own Methods, runs, members and model credit. You are its first member, and you keep your other workspaces.</p>
            <label htmlFor="workspace-name">Name</label>
            <input id="workspace-name" value={name} onChange={(event) => setName(event.target.value)} required maxLength={100} autoFocus />
            {error && <p role="alert">{error}</p>}
            <div className="dialog-actions">
              <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
              <Button type="submit" disabled={saving || !name.trim()}>Make workspace</Button>
            </div>
          </form>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** A page that is in another of the person's workspaces offers to switch there. */
export function OtherWorkspaceBanner() {
  const [workspace, setWorkspace] = useState<Workspace>();
  const path = useLocation({ select: (location) => location.pathname });
  useEffect(() => setWorkspace(undefined), [path]);
  useEffect(() => {
    const show = (event: Event) => setWorkspace((event as CustomEvent<Workspace>).detail);
    window.addEventListener(OTHER_WORKSPACE_EVENT, show);
    return () => window.removeEventListener(OTHER_WORKSPACE_EVENT, show);
  }, []);
  if (!workspace) return null;
  return (
    <div className="viewing-banner" role="status">
      <span>This is in your workspace <strong>{workspace.name}</strong>.</span>
      <button type="button" onClick={() => switchWorkspace(workspace)}>Switch to {workspace.name}</button>
    </div>
  );
}
